import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useState } from "react";
import AppSnackbar from "layouts/notifications/Snackbar";
import { API_BASE } from "config/api";
import { withAuthHeaders } from "services/authHeader";
import { apiFetch } from "services/apiFetch";

export default function DeleteStudent({ open, onClose, student, onSuccess }) {
  const userId = student?.id ?? null;
  const [loading, setLoading] = useState(false);

  // Snackbar for feedback
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });
  const showSnackbar = (type, message) => setSnackbar({ open: true, type, message });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  async function deleteStudent(studentId) {
    if (!studentId) throw new Error("Missing student id");
    const url = new URL("api/students/deleteStudentById", API_BASE);
    url.searchParams.set("student_id", studentId);

    const res = await apiFetch(url.toString(), {
      method: "PATCH",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();
    if (json?.status !== "success") {
      throw new Error(json?.message || "Delete failed");
    }
    return json;
  }

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const result = await deleteStudent(userId);
      showSnackbar("success", result.message || "Student deleted successfully");
      onSuccess?.(); // refresh parent list
      onClose();
    } catch (e) {
      console.error("Delete failed:", e);
      showSnackbar("error", e?.message || "Failed to delete student");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <MDBox p={3}>
          {/* Header */}
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">Delete Student</MDTypography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* Confirmation Body */}
          <DialogContent sx={{ backgroundColor: "#f8f9fa", p: 2 }}>
            <MDTypography sx={{ fontSize: "16px" }}>
              Are you sure you want to delete this student?
            </MDTypography>

            {/* Actions */}
            <DialogActions sx={{ mt: 2 }}>
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c82333" },
                }}
              >
                {loading ? "Deleting..." : "Yes, Delete"}
              </Button>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  color: "#6c757d",
                  borderColor: "#dee2e6",
                  textTransform: "none",
                  "&:hover": { borderColor: "#ced4da" },
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogContent>
        </MDBox>
      </Dialog>

      {/* Snackbar */}
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

/* ✅ Prop Types */
DeleteStudent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};
